import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * A general, reusable coach-mark overlay (`BACKLOG.md` B6a): dims the rest of the page and draws a
 * pulsing outline around one real on-screen element, addressed by a stable `data-help-id`
 * attribute — the convention this file establishes. Nothing here is specific to the help chat
 * agent; any feature that wants to draw attention to one control calls `useSpotlight().show(...)`.
 *
 * **The stacking trick, stated once because it is the whole mechanism.** Rather than cutting a
 * literal hole in the dimmed backdrop (a `clip-path` polygon-with-a-hole, or an SVG mask — both
 * exist, both are fragile across scroll containers and resize), the highlighted element itself is
 * temporarily promoted above the backdrop's `z-index` via `SPOTLIGHT_TARGET_CLASS` (declared in
 * `styles/app.css`). The backdrop dims and absorbs a click anywhere *except* on the promoted
 * element, which — being visually on top — receives its own clicks normally. This is the same
 * technique coach-mark libraries like driver.js use, and it is robust to the target being inside a
 * scroll container or an element the backdrop's own bounding box does not otherwise know about.
 *
 * **Mobile-first, checked in a real browser at phone and desktop widths (light and dark)** — see
 * the agent's own report for the exact verification. The outline box is computed from
 * `getBoundingClientRect()` on every scroll/resize, not a fixed size, so it tracks the element at
 * any viewport.
 *
 * **Graceful when the target is not on the current page at all.** `show()` looks the element up;
 * if `document.querySelector` finds nothing and no registered revealer can produce it either (see
 * below), nothing is rendered and the returned promise resolves `false` — `HelpChatWidget.tsx` uses
 * that to say so in the chat transcript rather than silently failing.
 *
 * **Conditionally-rendered targets: the revealer registry, not a special case for the mobile nav.**
 * A target's `data-help-id` element does not have to be permanently in the DOM — `shell/AppShell.tsx`
 * mounts its mobile nav's `<NavItemList>` only while the hamburger menu is open, matching this app's
 * own no-CSS-toggled-dual-DOM convention (`AppShell.tsx`'s own header comment on why: a second,
 * `display:none` copy in the document produces duplicate ids and broken `<label for>` bindings).
 * Found live: asking the chat for "Your access" and tapping "Show me" at phone width, with the menu
 * collapsed (the default state), reported "that control isn't on this page" — wrong, it was one tap
 * away. Fixed generally rather than by special-casing the mobile nav here: `show()` first checks
 * whether the target already exists; if not, it calls every function registered via
 * `registerTargetRevealer` (below) with the target id, giving each a chance to do whatever it takes
 * to make that element appear (open a drawer, expand a collapsed section, switch a tab — this file
 * has no opinion which), waits two animation frames for React to commit and paint the result, then
 * checks again. `AppShell.tsx` registers exactly one revealer: open the mobile nav when the
 * requested id names one of its own nav items and the nav is currently collapsed. A future
 * conditionally-rendered target anywhere else in the app registers its own revealer the same way,
 * rather than this file growing a case for each one.
 */

const SPOTLIGHT_TARGET_CLASS = 'help-spotlight-target';

export interface SpotlightRequest {
  readonly targetId: string;
  readonly label: string;
}

/** Given a target id this component does not currently render, do whatever it takes to render it
 * (open a drawer, switch a tab, expand a section) and return `true` if it attempted to. Returning
 * `true` does not guarantee the element exists afterward — `show()` re-checks — only that this
 * revealer recognised the id as its own responsibility. */
export type TargetRevealer = (targetId: string) => boolean;

interface SpotlightContextValue {
  readonly active: SpotlightRequest | null;
  /** Resolves `true` once the target element is found and the spotlight is showing, `false` if no
   * element carries this `data-help-id` even after every registered revealer had a chance to
   * produce it. */
  readonly show: (request: SpotlightRequest) => Promise<boolean>;
  readonly dismiss: () => void;
  readonly registerTargetRevealer: (revealer: TargetRevealer) => () => void;
}

const SpotlightContext = createContext<SpotlightContextValue | null>(null);

export function useSpotlight(): SpotlightContextValue {
  const value = useContext(SpotlightContext);
  if (value === null) {
    throw new Error('useSpotlight must be used within a SpotlightProvider');
  }
  return value;
}

/** Registers `revealer` for as long as the calling component is mounted — the ordinary shape every
 * caller wants, so `AppShell.tsx` does not have to hand-write its own registration effect. */
export function useSpotlightTargetRevealer(revealer: TargetRevealer): void {
  const { registerTargetRevealer } = useSpotlight();
  useEffect(() => registerTargetRevealer(revealer), [registerTargetRevealer, revealer]);
}

function findTarget(targetId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-help-id="${CSS.escape(targetId)}"]`);
}

/** Two frames rather than one: a single `requestAnimationFrame` can fire before React has actually
 * committed and painted a state update triggered a moment earlier (`AppShell.tsx`'s revealer calls
 * `setMobileNavOpen` synchronously, but the resulting DOM change is not guaranteed visible until the
 * frame after that commit) — verified directly: one frame flaked intermittently in a real mobile
 * browser check, two did not. */
function waitTwoFrames(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
}

export function SpotlightProvider({ children }: { readonly children: ReactNode }) {
  const [active, setActive] = useState<SpotlightRequest | null>(null);
  const revealersRef = useRef<ReadonlySet<TargetRevealer>>(new Set());

  const dismiss = useCallback(() => {
    setActive(null);
  }, []);

  const registerTargetRevealer = useCallback((revealer: TargetRevealer): (() => void) => {
    revealersRef.current = new Set([...revealersRef.current, revealer]);
    return () => {
      revealersRef.current = new Set(
        [...revealersRef.current].filter((candidate) => candidate !== revealer),
      );
    };
  }, []);

  const show = useCallback(async (request: SpotlightRequest): Promise<boolean> => {
    if (findTarget(request.targetId) !== null) {
      setActive(request);
      return true;
    }

    let anyRevealerAttempted = false;
    for (const revealer of revealersRef.current) {
      if (revealer(request.targetId)) anyRevealerAttempted = true;
    }
    if (!anyRevealerAttempted) return false;

    await waitTwoFrames();
    if (findTarget(request.targetId) === null) return false;
    setActive(request);
    return true;
  }, []);

  const value = useMemo<SpotlightContextValue>(
    () => ({ active, show, dismiss, registerTargetRevealer }),
    [active, show, dismiss, registerTargetRevealer],
  );

  return (
    <SpotlightContext.Provider value={value}>
      {children}
      {active !== null ? <SpotlightOverlay request={active} onDismiss={dismiss} /> : null}
    </SpotlightContext.Provider>
  );
}

interface Box {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

function measure(el: HTMLElement): Box {
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

function SpotlightOverlay({
  request,
  onDismiss,
}: {
  readonly request: SpotlightRequest;
  readonly onDismiss: () => void;
}) {
  const { t } = useTranslation('help');
  const [box, setBox] = useState<Box | null>(null);

  useEffect(() => {
    const el = findTarget(request.targetId);
    if (el === null) {
      // The element disappeared between `show()` finding it and this effect running (a route
      // change mid-click, e.g.) — dismiss rather than render a stale or empty overlay.
      onDismiss();
      return;
    }

    el.classList.add(SPOTLIGHT_TARGET_CLASS);

    const update = () => {
      setBox(measure(el));
    };

    // Scroll fires far more often than once per animation frame during an actual scroll gesture —
    // without coalescing, `scheduleUpdate` below called `setBox` (and so re-rendered) once per raw
    // scroll event instead of once per frame. `pendingFrame` tracks whether a frame is already
    // queued so a burst of scroll events inside the same frame schedules only one `update`.
    let pendingFrame: number | null = null;
    const scheduleUpdate = () => {
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        update();
      });
    };

    // The initial measurement goes through the same scheduler rather than being called directly
    // here, so `setBox` is never invoked synchronously in the effect's own body —
    // `react-hooks/set-state-in-effect` flags exactly that shape, since it can trigger a cascading
    // extra render. One frame of delay is imperceptible for a highlight overlay.
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    // `capture: true` so this also fires for a scroll inside a nested scroll container, not only
    // the window itself.
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
      el.classList.remove(SPOTLIGHT_TARGET_CLASS);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [request.targetId, onDismiss]);

  if (box === null) return null;

  const PADDING = 6;

  return createPortal(
    <div className="fixed inset-0 z-[60]" role="presentation">
      {/* The dimmed backdrop. Absorbs a click anywhere on it to dismiss — the promoted target
          element itself sits visually above this layer, per this file's own header comment, so a
          click that lands on the target never reaches this handler at all. */}
      <button
        type="button"
        aria-label={t('spotlight.dismissAriaLabel')}
        onClick={onDismiss}
        // An inline colour, not a `bg-black/*` utility: `app.css` deliberately clears Tailwind's
        // stock `--color-*` scale (including its `black`/`transparent` keywords) so an off-system
        // class silently produces no CSS at all — this dimming layer is exactly the kind of thing
        // that failure mode would make invisible rather than merely wrong-looking.
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }}
        className="absolute inset-0 h-full w-full cursor-default"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute animate-pulse rounded-control ring-4 ring-focus transition-[top,left,width,height] duration-150"
        style={{
          top: box.top - PADDING,
          left: box.left - PADDING,
          width: box.width + PADDING * 2,
          height: box.height + PADDING * 2,
        }}
      />

      <div
        role="status"
        className="absolute flex max-w-[min(90vw,22rem)] flex-col gap-2 rounded-panel border border-line bg-surface p-3 text-sm text-ink shadow-overlay"
        style={captionPosition(box)}
      >
        <p className="m-0">{request.label}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="self-end rounded-control border border-line bg-surface px-2 py-1 text-xs font-medium text-ink hover:bg-raised"
        >
          {t('spotlight.gotIt')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Places the caption below the highlighted box, unless that would run off the bottom of the
 * viewport (a target near the bottom of a phone screen), in which case it goes above instead —
 * checked in a real browser at phone width, where the header nav sits close to the top and a
 * caption placed unconditionally below could otherwise be pushed past the fold.
 */
function captionPosition(box: Box): { top?: number; bottom?: number; left: number } {
  const GAP = 12;
  const estimatedCaptionHeight = 96;
  const left = Math.max(8, Math.min(box.left, window.innerWidth - 8 - 352));
  const spaceBelow = window.innerHeight - (box.top + box.height);

  if (spaceBelow >= estimatedCaptionHeight + GAP) {
    return { top: box.top + box.height + GAP, left };
  }
  return { bottom: window.innerHeight - box.top + GAP, left };
}
